import React, { useState, useCallback } from 'react';
import { Box, Heading, HStack, VStack, Button, Input, Textarea, Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, IconButton, Text, Select, Badge, Flex, Tag, CloseButton, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { FiPlus, FiTrash2, FiX, FiArrowUp, FiArrowDown, FiMove } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Sortable Section Component
const SortableSection = ({ section, index, onRemove, onMoveUp, onMoveDown, onUpdateMargin, onUpdateName, isFirst, isLast, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef} 
      style={style}
      p={3} 
      bg="gray.50" 
      borderRadius="md"
      border={isDragging ? "2px dashed" : "1px solid"}
      borderColor={isDragging ? "blue.300" : "gray.200"}
      mb={2}
    >
      <VStack align="stretch" spacing={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Box 
              {...attributes} 
              {...listeners}
              cursor="grab"
              _active={{ cursor: "grabbing" }}
              p={1}
              borderRadius="sm"
              _hover={{ bg: "gray.100" }}
            >
              <FiMove color="gray.500" />
            </Box>
            <Text fontSize="xs" color="gray.500" minW="20px" textAlign="center">
              {index + 1}
            </Text>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {section.name_en || section.name_de || section.name || t('settings.unknown_label')}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <IconButton
              size="xs"
              icon={<FiArrowUp />}
              onClick={() => onMoveUp(index)}
              isDisabled={isFirst}
              variant="ghost"
              colorScheme="blue"
            />
            <IconButton
              size="xs"
              icon={<FiArrowDown />}
              onClick={() => onMoveDown(index)}
              isDisabled={isLast}
              variant="ghost"
              colorScheme="blue"
            />
            <CloseButton 
              size="sm" 
              onClick={() => onRemove(index)}
              color="red.500"
            />
          </HStack>
        </Flex>
        <HStack spacing={3} align="flex-end">
          <FormControl flex={1}>
            <FormLabel fontSize="xs">{t('settings.english_label')}</FormLabel>
            <Input
              size="sm"
              value={section.name_en || ''}
              onChange={(e) => onUpdateName(index, 'name_en', e.target.value)}
              placeholder={t('settings.english_title_placeholder')}
            />
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="xs">{t('settings.german_label')}</FormLabel>
            <Input
              size="sm"
              value={section.name_de || ''}
              onChange={(e) => onUpdateName(index, 'name_de', e.target.value)}
              placeholder={t('settings.german_title_placeholder')}
            />
          </FormControl>
          <FormControl maxW="120px">
            <FormLabel fontSize="xs">{t('settings.margin_label')}</FormLabel>
            <HStack spacing={1} align="center">
              <Input
                size="sm"
                type="number"
                step="0.01"
                min="0"
                value={section.margin || ''}
                onChange={(e) => onUpdateMargin(index, e.target.value)}
                placeholder="0.00"
              />
              <Text fontSize="xs" color="gray.500">%</Text>
            </HStack>
          </FormControl>
        </HStack>
      </VStack>
    </Box>
  );
};

const SettingsTemplates = () => {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [q, setQ] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sections: [], usages: {}, pdf_template_id: '', notes: '', footer_text_en: '', footer_text_de: '' });
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, templateId: null, templateName: '' });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { data: templates } = useQuery(['proposal_templates', q, page, pageSize], () => api.get(`/api/settings/templates?q=${encodeURIComponent(q)}&limit=${pageSize}&offset=${page*pageSize}`).then(res => res.data.data.templates));
  const { data: sections } = useQuery('sections', () => api.get('/api/meta/sections').then(res => res.data.data.sections));
  const { data: pdfTemplates } = useQuery('pdf_templates', () => api.get('/api/settings/pdf/templates').then(res => res.data.data.templates));

  const createTemplate = useMutation((data) => api.post('/api/settings/templates', data), { onSuccess: () => { queryClient.invalidateQueries(['proposal_templates', q]); setIsOpen(false); setForm({ name: '', sections: [], margin: '', usages: {}, notes: '' }); toast({ title: t('common.success'), status: 'success' }); }});
  const updateTemplate = useMutation(({ id, data }) => api.put(`/api/settings/templates/${id}`, data), { onSuccess: () => { queryClient.invalidateQueries(['proposal_templates', q]); setIsOpen(false); setEditing(null); toast({ title: t('common.success'), status: 'success' }); }});
  const deleteTemplate = useMutation((id) => api.delete(`/api/settings/templates/${id}`), { onSuccess: () => { queryClient.invalidateQueries(['proposal_templates', q]); toast({ title: t('common.success'), status: 'success' }); }});

  const openCreate = () => { setEditing(null); setForm({ name: '', sections: [], usages: {}, pdf_template_id: '', notes: '', footer_text_en: '', footer_text_de: '' }); setIsOpen(true); };

  // Memoized handlers for ReactQuill to prevent infinite loops
  const handleFooterEnChange = useCallback((value) => {
    setForm(prev => ({ ...prev, footer_text_en: value }));
  }, []);

  const handleFooterDeChange = useCallback((value) => {
    setForm(prev => ({ ...prev, footer_text_de: value }));
  }, []);
  const openEdit = (tpl) => { 
    setEditing(tpl); 
    // Convert old format (name) to new format (name_en, name_de) for backward compatibility
    const sections = (tpl.json?.sections || []).map(sec => {
      if (sec.name && !sec.name_en && !sec.name_de) {
        // Old format: migrate name to both languages
        return {
          ...sec,
          name_en: sec.name,
          name_de: sec.name
        };
      }
      return sec;
    });
    setForm({ 
      name: tpl.name || '', 
      sections: sections, 
      usages: tpl.json?.usages || {}, 
      pdf_template_id: tpl.pdf_template_id || '', 
      notes: tpl.json?.notes || '',
      footer_text_en: tpl.footer_text_en || '',
      footer_text_de: tpl.footer_text_de || ''
    }); 
    setIsOpen(true); 
  };

  // Helper functions for sections
  const getSectionName = (sectionId) => {
    if (!sections) return 'Unknown';
    const section = sections.find(s => s.id === sectionId);
    return section ? (section.name_en || section.name_de || section.code || 'Unknown') : 'Unknown';
  };

  const addSectionBlock = () => {
    if (!selectedSectionId) return;
    const sec = sections?.find(s => String(s.id) === String(selectedSectionId));
    if (!sec) return;
    setForm(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { 
        section_id: sec.id, 
        name_en: sec.name_en || '',
        name_de: sec.name_de || '',
        margin: 1.0 // Default margin
      }]
    }));
    setSelectedSectionId('');
  };

  const [selectedSectionId, setSelectedSectionId] = useState('');

  const removeSectionFromTemplate = (index) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSectionMargin = (index, margin) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.map((sec, i) => 
        i === index ? { ...sec, margin: margin === '' ? null : Number(margin) } : sec
      )
    }));
  };

  const updateSectionName = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      sections: prev.sections.map((sec, i) => 
        i === index ? { ...sec, [field]: value } : sec
      )
    }));
  };

  const moveSectionUp = (index) => {
    if (index === 0) return; // Zaten en üstte
    setForm(prev => {
      const newSections = [...prev.sections];
      [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
      return { ...prev, sections: newSections };
    });
  };

  const moveSectionDown = (index) => {
    setForm(prev => {
      if (index === prev.sections.length - 1) return prev; // Zaten en altta
      const newSections = [...prev.sections];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      return { ...prev, sections: newSections };
    });
  };

  // Drag & Drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setForm(prev => ({
        ...prev,
        sections: arrayMove(prev.sections, active.id, over.id)
      }));
    }
  };

  // Usage management functions
  const addUsage = () => {
    const newKey = `usage_${Object.keys(form.usages || {}).length + 1}`;
    setForm(prev => ({
      ...prev,
      usages: {
        ...prev.usages,
        [newKey]: { 
          'lang.en': 'One Time Use',
          'lang.de': 'Einsatz-1',
          ratio: 1.0 
        }
      }
    }));
  };

  const updateUsageLang = (usageKey, lang, value) => {
    setForm(prev => ({
      ...prev,
      usages: {
        ...prev.usages,
        [usageKey]: {
          ...prev.usages[usageKey],
          [`lang.${lang}`]: value
        }
      }
    }));
  };

  const updateUsageRatio = (usageKey, ratio) => {
    setForm(prev => ({
      ...prev,
      usages: {
        ...prev.usages,
        [usageKey]: {
          ...prev.usages[usageKey],
          ratio: Number(ratio) || 0
        }
      }
    }));
  };

  const removeUsage = (usageKey) => {
    setForm(prev => {
      const newUsages = { ...prev.usages };
      delete newUsages[usageKey];
      return { ...prev, usages: newUsages };
    });
  };

  const saveTemplate = () => {
    if (!form.name) { toast({ title: t('common.error'), description: 'Name required', status: 'error' }); return; }
    const payload = { ...form };
    if (typeof payload.sections === 'string') {
      try { payload.sections = JSON.parse(payload.sections || '[]'); } catch { payload.sections = []; }
    }
    
    // Ensure usages is properly formatted
    if (typeof payload.usages === 'string') {
      try { payload.usages = JSON.parse(payload.usages || '{}'); } catch { payload.usages = {}; }
    }
    
    if (editing) updateTemplate.mutate({ id: editing.id, data: payload }); else createTemplate.mutate(payload);
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('settings.templates_title')}</Heading>
        <HStack>
          <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} maxW="250px" />
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>{t('settings.new_template')}</Button>
        </HStack>
      </HStack>

      <Card>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('settings.table_id')}</Th>
                <Th>{t('settings.table_name')}</Th>
                <Th>{t('settings.table_sections')}</Th>
                <Th>{t('settings.table_usages')}</Th>
                <Th>{t('settings.table_created')}</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {templates?.map(tpl => (
                <Tr key={tpl.id} bg={tpl.id === 1 ? "blue.50" : undefined}>
                  <Td>
                    <HStack>
                      <Text>{tpl.id}</Text>
                      {tpl.id === 1 && (
                        <Badge colorScheme="blue" variant="solid" size="sm">
                          {t('settings.default_badge')}
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Text>{tpl.name}</Text>
                      {tpl.id === 1 && (
                        <Badge colorScheme="green" variant="subtle" size="sm">
                          {t('settings.protected_badge')}
                        </Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    {tpl.json && Array.isArray(tpl.json.sections) && tpl.json.sections.length > 0 ? (
                      <VStack align="start" spacing={1}>
                        {tpl.json.sections.map((sec, idx) => {
                          // Support both old format (name) and new format (name_en, name_de)
                          const displayName = currentLanguage === 'de' 
                            ? (sec.name_de || sec.name || getSectionName(sec.section_id) || 'Unknown')
                            : (sec.name_en || sec.name || getSectionName(sec.section_id) || 'Unknown');
                          return (
                            <Badge key={idx} colorScheme="blue" variant="subtle" size="sm">
                              {displayName} (Margin: {sec.margin || 0}%)
                            </Badge>
                          );
                        })}
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">{t('settings.no_sections')}</Text>
                    )}
                  </Td>

                  <Td>
                    {tpl.json && tpl.json.usages && Object.keys(tpl.json.usages).length > 0 ? (
                      <VStack align="start" spacing={1}>
                        {Object.entries(tpl.json.usages).map(([usageKey, usage]) => (
                          <Badge key={usageKey} colorScheme="green" variant="subtle" size="sm">
                            {usage['lang.en'] || usage['lang.de'] || usageKey}: {usage.ratio}
                          </Badge>
                        ))}
                      </VStack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">{t('settings.no_usages')}</Text>
                    )}
                  </Td>
                  <Td>{new Date(tpl.created_at).toLocaleString()}</Td>
                  <Td>
                    <HStack>
                      <Button size="xs" variant="outline" onClick={() => openEdit(tpl)}>{t('common.edit')}</Button>
                      <IconButton 
                        aria-label={t('settings.delete_label')} 
                        size="xs" 
                        colorScheme="red" 
                        icon={<FiTrash2 />} 
                        onClick={() => setDeleteConfirm({ 
                          isOpen: true, 
                          templateId: tpl.id, 
                          templateName: tpl.name 
                        })} 
                        isDisabled={tpl.id === 1}
                        title={tpl.id === 1 ? t('settings.default_template_protected') : ''}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <HStack justify="space-between" mt={4}>
            <HStack>
              <Button size="sm" onClick={() => setPage(p => Math.max(0, p-1))} isDisabled={page===0}>{t('settings.prev_button')}</Button>
              <Button size="sm" onClick={() => setPage(p => p+1)} isDisabled={(templates?.length||0) < pageSize}>{t('settings.next_button')}</Button>
            </HStack>
            <HStack>
              <Text fontSize="sm">{t('settings.rows_label')}</Text>
              <Select size="sm" value={pageSize} onChange={(e)=>{setPage(0); setPageSize(Number(e.target.value));}} maxW="80px">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Create/Edit Template */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? t('common.edit') : t('common.new')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('settings.name_field')}</FormLabel>
                <Input value={form.name} onChange={(e)=> setForm({ ...form, name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.pdf_template_field')}</FormLabel>
                <Select value={form.pdf_template_id || ''} onChange={(e)=> setForm({ ...form, pdf_template_id: e.target.value })}>
                  <option value="">{t('settings.select_option')}</option>
                  {pdfTemplates?.map(pt => (<option key={pt.id} value={pt.id}>{pt.name}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('settings.notes_field')}</FormLabel>
                <Textarea rows={3} value={form.notes || ''} onChange={(e)=> setForm({ ...form, notes: e.target.value })} />
              </FormControl>

              <FormControl>
                <FormLabel>{t('settings.footer_text_en', 'Footer Text (English)')}</FormLabel>
                <Box border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                  <ReactQuill
                    theme="snow"
                    value={form.footer_text_en || ''}
                    onChange={handleFooterEnChange}
                    placeholder={t('settings.footer_text_en_placeholder', 'Enter footer text in English...')}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    style={{ minHeight: '200px' }}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('settings.footer_text_hint', 'This footer will appear at the bottom of proposals using this template.')}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>{t('settings.footer_text_de', 'Footer Text (German)')}</FormLabel>
                <Box border="1px" borderColor="gray.200" borderRadius="md" bg="white">
                  <ReactQuill
                    theme="snow"
                    value={form.footer_text_de || ''}
                    onChange={handleFooterDeChange}
                    placeholder={t('settings.footer_text_de_placeholder', 'Enter footer text in German...')}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    style={{ minHeight: '200px' }}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('settings.footer_text_hint', 'This footer will appear at the bottom of proposals using this template.')}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>{t('settings.sections_field')}</FormLabel>
                <VStack align="stretch" spacing={3}>
                  {/* Section Selection */}
                  <HStack>
                    <Select 
                      placeholder={t('settings.select_section_to_add')} 
                      value={selectedSectionId}
                      onChange={(e) => setSelectedSectionId(e.target.value)}
                      size="sm"
                    >
                      {sections?.map(sec => (
                        <option key={sec.id} value={sec.id}>
                          {sec.name_en || sec.name_de || sec.code || `Section ${sec.id}`}
                        </option>
                      ))}
                    </Select>
                    <Button onClick={addSectionBlock} isDisabled={!selectedSectionId} size="sm" height="32px">
                      {t('settings.add_section_button')}
                    </Button>
                  </HStack>
                  
                  {/* Current Sections */}
                  {form.sections && form.sections.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      <Text fontSize="sm" fontWeight="medium">{t('settings.current_sections')}</Text>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={form.sections.map((_, idx) => idx)}
                          strategy={verticalListSortingStrategy}
                        >
                          {form.sections.map((sec, idx) => (
                            <SortableSection
                              key={idx}
                              section={sec}
                              index={idx}
                              onRemove={removeSectionFromTemplate}
                              onMoveUp={moveSectionUp}
                              onMoveDown={moveSectionDown}
                              onUpdateMargin={updateSectionMargin}
                              onUpdateName={updateSectionName}
                              isFirst={idx === 0}
                              isLast={idx === form.sections.length - 1}
                              t={t}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </VStack>
                  )}
                </VStack>
              </FormControl>

              {/* Usages Management */}
              <FormControl>
                <FormLabel>{t('settings.usage_multipliers')}</FormLabel>
                <VStack align="stretch" spacing={3}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">{t('settings.usage_multipliers_description')}</Text>
                    <Button size="sm" onClick={addUsage} leftIcon={<FiPlus />}>
                      {t('settings.add_usage_button')}
                    </Button>
                  </HStack>
                  
                  {Object.keys(form.usages || {}).length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      {Object.entries(form.usages || {}).map(([usageKey, usage]) => (
                        <Box key={usageKey} p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                          <VStack align="stretch" spacing={3}>
                            {/* Usage Key */}
                            <Text fontSize="xs" color="gray.600" fontWeight="medium">
                              {t('settings.usage_key_label')} {usageKey}
                            </Text>
                            
                            {/* Language Inputs */}
                            <HStack spacing={3}>
                              <FormControl>
                                <FormLabel fontSize="xs">{t('settings.english_label')}</FormLabel>
                                <Input
                                  value={usage['lang.en'] || ''}
                                  onChange={(e) => updateUsageLang(usageKey, 'en', e.target.value)}
                                  placeholder={t('settings.english_title_placeholder')}
                                  size="sm"
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs">{t('settings.german_label')}</FormLabel>
                                <Input
                                  value={usage['lang.de'] || ''}
                                  onChange={(e) => updateUsageLang(usageKey, 'de', e.target.value)}
                                  placeholder={t('settings.german_title_placeholder')}
                                  size="sm"
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="xs">{t('settings.ratio_label')}</FormLabel>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={usage.ratio || ''}
                                  onChange={(e) => updateUsageRatio(usageKey, e.target.value)}
                                  placeholder="1.00"
                                  size="sm"
                                  maxW="100px"
                                />
                              </FormControl>
                            </HStack>
                            
                            {/* Remove Button */}
                            <Flex justify="flex-end">
                              <IconButton
                                size="sm"
                                icon={<FiX />}
                                onClick={() => removeUsage(usageKey)}
                                colorScheme="red"
                                variant="ghost"
                              />
                            </Flex>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </FormControl>

              {/* Combined JSON Preview (Read-only) - At the bottom */}
              <FormControl>
                <FormLabel fontSize="sm" color="gray.600">{t('settings.json_preview_label')}</FormLabel>
                <Textarea 
                  value={JSON.stringify({
                    sections: form.sections,
                    usages: form.usages
                  }, null, 2)} 
                  isReadOnly 
                  size="sm"
                  fontSize="xs"
                  bg="gray.50"
                  minH="120px"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsOpen(false)}>{t('settings.cancel_button')}</Button>
            <Button colorScheme="brand" onClick={() => {
              const payload = { 
                name: form.name, 
                sections: form.sections, 
                usages: form.usages, 
                pdf_template_id: form.pdf_template_id || null, 
                notes: form.notes || '',
                footer_text_en: form.footer_text_en || null,
                footer_text_de: form.footer_text_de || null
              };
              if (editing) updateTemplate.mutate({ id: editing.id, data: payload }); else createTemplate.mutate(payload);
            }}>{t('settings.save_button')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' })}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('settings.delete_template_title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('settings.delete_template_confirm')}
              <br />
              <Text fontWeight="semibold" color="red.500" mt={2}>
                "{deleteConfirm.templateName}"
              </Text>
              <Text fontSize="sm" color="gray.600" mt={2}>
                {t('settings.delete_template_warning')}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={() => setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' })}>
                {t('settings.cancel_button')}
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => {
                  deleteTemplate.mutate(deleteConfirm.templateId);
                  setDeleteConfirm({ isOpen: false, templateId: null, templateName: '' });
                }} 
                ml={3}
              >
                {t('common.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SettingsTemplates;
